import * as Sentry from '@sentry/react';
import { severityLevelFromString } from '@sentry/utils';
import { camelCase, get } from 'lodash';
import pino from 'pino';
import { ApplicationConfig } from '../config/config';

/**
 * For performance, only log to the console when running locally.
 */
const isConsoleLoggingEnabled = ApplicationConfig.env === 'local';

type ConsoleLogger = Pick<
  Console,
  'trace' | 'debug' | 'info' | 'log' | 'warn' | 'error'
>;

type ConsoleLogLevel = keyof ConsoleLogger;

const consoleLogLevels = new Array<ConsoleLogLevel>(
  'trace',
  'debug',
  'info',
  'log',
  'warn',
  'error'
);

const isConsoleLogLevel = (level: string): level is ConsoleLogLevel => {
  return consoleLogLevels.includes(level as ConsoleLogLevel);
};

const pinoLogger = pino({
  /**
   * The web package only runs in the browser.
   * Only pino's browser config is used.
   * http://getpino.io/#/docs/browser
   */
  browser: {
    /**
     * Disable pino's default console logging because it doesn't
     * apply any of the custom formatters or serializers to the data.
     * Instead, use the `transmit` hook, which receives both the
     * log message (string) and any supplemental log data (object).
     */
    disabled: true,
    write: () => {
      // Despite disabling pino's default console logging, it still occurs.
      // We blank this method to ensure we have full control of logging.
      // See `transmit.send` method instead.
    },
    /**
     * Hook to send log messages to a remote endpoint, such as Sentry.
     */
    transmit: {
      /**
       * Minimum log level to transmit.
       */
      level: import.meta.env.VITE_LOG_LEVEL ?? 'info',
      /**
       * Send (or not) a message somewhere.
       */
      send(level, logEvent) {
        // The messages array is the arguments passed to the log method.
        // The first argument is conventionally the string message.
        // The second argument is conventionally an object of context data.
        // Example:
        //    logger.info(message, data)
        //    logger.info('hello', { world: 42 })
        //
        const [message, data] = logEvent.messages;

        const logData = formatLogData({
          ...data,
          timestamp: new Date(logEvent.ts).toISOString(),
        });

        if (isConsoleLoggingEnabled && isConsoleLogLevel(level)) {
          console[level](message, logData);
        }

        if (message) {
          logToSentry({ level, message, extra: logData });
        }
      },
    },
  },
});

/**
 * Formatters provide low-level access to format the log line shape.
 * https://getpino.io/#/docs/api?id=formatters-object
 */
const formatLogData = (
  logData: Record<string, unknown>
): Record<string, unknown> => {
  // Non-serializable objects must be formatted as strings explicitly.
  // For example, this mitigates error objects being logged as "{}".
  for (const entry of Object.entries(logData)) {
    const [key, value] = entry;
    if (value instanceof Error) {
      // Instead of { "error": errorObj } serializing to {}
      // we now get { "error": "it broke", "errorCode": 42, ... }
      logData[key] = value.message;
      ['name', 'code'].forEach((errProp) => {
        const errValue = get(value, errProp);
        if (errValue) {
          errProp = camelCase(`${key}_${errProp}`);
          logData[errProp] = errValue;
        }
      });
    } else if (value instanceof Event) {
      logData[key] = value.constructor.name;
      ['code', 'type', 'reason'].forEach((evtProp) => {
        const evtValue = get(value, evtProp);
        if (evtValue) {
          evtProp = camelCase(`${key}_${evtProp}`);
          logData[evtProp] = evtValue;
        }
      });
    } else if (value instanceof Set) {
      logData[key] = Array.from(value.keys());
    } else if (value instanceof Map) {
      logData[key] = Array.from(value.entries());
    }
  }
  return logData;
};

const logToSentry = (options: {
  level: pino.Level;
  message: string;
  extra?: Record<string, unknown>;
}) => {
  const { level, message, extra } = options;
  if (Sentry) {
    const sentryLevel = severityLevelFromString(level);
    if (sentryLevel === 'error' || sentryLevel === 'fatal') {
      Sentry.captureMessage(message, {
        level: sentryLevel,
        extra,
      });
    } else {
      Sentry.addBreadcrumb({
        message,
        level: sentryLevel,
        data: extra,
      });
    }
  }
};

export { pinoLogger as logger };
